/**
 * ai-assistant i18n — FR default + AR (RTL). Registered once in the module's onRegister(). Keys are
 * namespaced under `ai.*` and `nav.ai.*`. Layout mirrors automatically under dir="rtl" via CSS logical
 * properties; codes / versions stay LTR with .nv-ltr-island. Missing keys fall back FR → raw key.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // nav
  "nav.section.assist": "Assistance",
  "nav.ai.assistant": "Assistant opérationnel",
  "nav.ai.instructions": "Instructions du laboratoire",

  // shared
  "ai.action.refresh": "Actualiser",
  "ai.action.cancel": "Annuler",
  "ai.action.retry": "Réessayer",

  // module-absent (cloud-only / un-mounted install)
  "ai.unavailable.title": "Indisponible sur cette installation",
  "ai.unavailable.desc": "Le module assistant n'est pas activé sur cette installation. Contactez votre administrateur.",

  // answer status chips (short — the inline notice carries the full title)
  "ai.status.ok": "Réponse",
  "ai.status.abstenu": "Hors périmètre",
  "ai.status.disabled": "Désactivé",
  "ai.status.error": "Indisponible",

  // ── Assistant (conversational helpdesk) ─────────────────────────────────────────
  "ai.assistant.title": "Assistant opérationnel",
  "ai.assistant.subtitle": "Aide à l'utilisation du logiciel et aux procédures du laboratoire. Non clinique.",
  "ai.assistant.start": "Démarrer une session",
  "ai.assistant.restart": "Nouvelle session",
  "ai.assistant.session": "Session",
  "ai.assistant.empty.title": "Posez une question opérationnelle",
  "ai.assistant.empty.desc": "Comment utiliser un écran, une procédure interne, le catalogue… L'assistant ne pose pas de diagnostic et n'interprète aucun résultat.",
  "ai.assistant.placeholder": "Votre question (procédure, écran, SOP)…",
  "ai.assistant.send": "Envoyer",
  "ai.assistant.sending": "Réflexion…",
  "ai.assistant.you": "Vous",
  "ai.assistant.assistant": "Assistant",
  "ai.assistant.opening": "Ouverture de la session…",
  "ai.assistant.openError": "Impossible d'ouvrir une session. Réessayez.",
  "ai.assistant.sendError": "L'envoi a échoué. Réessayez.",
  "ai.assistant.hint": "Entrée pour envoyer · Maj+Entrée pour un retour à la ligne",

  // citations
  "ai.assistant.citations.title": "Sources citées",
  "ai.assistant.citations.none": "Aucune source citée",
  "ai.assistant.citations.count": "{n} source(s)",

  // abstenu / indisponible inline notices
  "ai.assistant.abstenu.title": "Demande hors périmètre",
  "ai.assistant.abstenu.desc": "L'assistant est non clinique. Pour toute interprétation, consultez le Biologiste.",
  "ai.assistant.disabled.title": "Assistant désactivé",
  "ai.assistant.disabled.desc": "L'assistant IA n'est pas configuré sur cette installation. Les instructions et le référentiel restent consultables.",
  "ai.assistant.error.title": "Assistant indisponible",
  "ai.assistant.error.desc": "Une erreur est survenue. L'assistant n'est jamais dans un chemin de contrôle opérationnel.",

  // disclaimer
  "ai.disclaimer.label": "Avertissement",

  // ── Instructions editor (lab-Admin) ─────────────────────────────────────────────
  "ai.instructions.title": "Instructions du laboratoire",
  "ai.instructions.subtitle": "Le instructions.md interne, ajouté en tête de chaque réponse de l'assistant. Versionné (ajout uniquement).",
  "ai.instructions.editor": "Éditeur",
  "ai.instructions.preview": "Aperçu",
  "ai.instructions.field": "Contenu (Markdown)",
  "ai.instructions.placeholder": "# Procédures internes\n\n- Règle 1…\n- Règle 2…",
  "ai.instructions.version": "Version {n}",
  "ai.instructions.versionNone": "Aucune version publiée",
  "ai.instructions.publish": "Publier la version",
  "ai.instructions.publishing": "Publication…",
  "ai.instructions.published": "Instructions publiées (version {n}).",
  "ai.instructions.publishError": "La publication a échoué.",
  "ai.instructions.empty.title": "Aucune instruction publiée",
  "ai.instructions.empty.desc": "Rédigez les procédures internes du laboratoire ci-dessous puis publiez-les.",
  "ai.instructions.required": "Le contenu est requis.",
  "ai.instructions.unchanged": "Aucune modification à publier.",
  "ai.instructions.dirty": "Modifications non publiées.",
  "ai.instructions.reset": "Réinitialiser",
  "ai.instructions.previewEmpty": "L'aperçu apparaîtra ici.",
  "ai.instructions.readonly": "Lecture seule — vous n'avez pas le droit de publier.",
};

const AR: Record<string, string> = {
  // nav
  "nav.section.assist": "المساعدة",
  "nav.ai.assistant": "المساعد التشغيلي",
  "nav.ai.instructions": "تعليمات المختبر",

  // shared
  "ai.action.refresh": "تحديث",
  "ai.action.cancel": "إلغاء",
  "ai.action.retry": "إعادة المحاولة",

  // module-absent
  "ai.unavailable.title": "غير متوفر على هذا التثبيت",
  "ai.unavailable.desc": "وحدة المساعد غير مفعّلة على هذا التثبيت. اتصل بالمسؤول.",

  // answer status chips (short — the inline notice carries the full title)
  "ai.status.ok": "إجابة",
  "ai.status.abstenu": "خارج النطاق",
  "ai.status.disabled": "معطّل",
  "ai.status.error": "غير متوفر",

  // assistant
  "ai.assistant.title": "المساعد التشغيلي",
  "ai.assistant.subtitle": "مساعدة في استخدام البرنامج وإجراءات المختبر. غير سريري.",
  "ai.assistant.start": "بدء جلسة",
  "ai.assistant.restart": "جلسة جديدة",
  "ai.assistant.session": "الجلسة",
  "ai.assistant.empty.title": "اطرح سؤالاً تشغيلياً",
  "ai.assistant.empty.desc": "كيفية استخدام شاشة، إجراء داخلي، الكتالوج… المساعد لا يشخّص ولا يفسّر أي نتيجة.",
  "ai.assistant.placeholder": "سؤالك (إجراء، شاشة، SOP)…",
  "ai.assistant.send": "إرسال",
  "ai.assistant.sending": "جارٍ التفكير…",
  "ai.assistant.you": "أنت",
  "ai.assistant.assistant": "المساعد",
  "ai.assistant.opening": "جارٍ فتح الجلسة…",
  "ai.assistant.openError": "تعذّر فتح جلسة. أعد المحاولة.",
  "ai.assistant.sendError": "فشل الإرسال. أعد المحاولة.",
  "ai.assistant.hint": "Enter للإرسال · Shift+Enter لسطر جديد",

  // citations
  "ai.assistant.citations.title": "المصادر المذكورة",
  "ai.assistant.citations.none": "لا توجد مصادر مذكورة",
  "ai.assistant.citations.count": "{n} مصدر/مصادر",

  // notices
  "ai.assistant.abstenu.title": "طلب خارج النطاق",
  "ai.assistant.abstenu.desc": "المساعد غير سريري. لأي تفسير، استشر البيولوجي.",
  "ai.assistant.disabled.title": "المساعد معطّل",
  "ai.assistant.disabled.desc": "المساعد الذكي غير مهيّأ على هذا التثبيت. تبقى التعليمات والمرجع قابلة للاطلاع.",
  "ai.assistant.error.title": "المساعد غير متوفر",
  "ai.assistant.error.desc": "حدث خطأ. المساعد ليس أبداً في مسار تحكّم تشغيلي.",

  // disclaimer
  "ai.disclaimer.label": "تنبيه",

  // instructions editor
  "ai.instructions.title": "تعليمات المختبر",
  "ai.instructions.subtitle": "ملف instructions.md الداخلي، يُضاف في رأس كل إجابة للمساعد. مُؤرشف بالإصدارات (إضافة فقط).",
  "ai.instructions.editor": "المحرّر",
  "ai.instructions.preview": "معاينة",
  "ai.instructions.field": "المحتوى (Markdown)",
  "ai.instructions.placeholder": "# الإجراءات الداخلية\n\n- القاعدة 1…\n- القاعدة 2…",
  "ai.instructions.version": "الإصدار {n}",
  "ai.instructions.versionNone": "لا يوجد إصدار منشور",
  "ai.instructions.publish": "نشر الإصدار",
  "ai.instructions.publishing": "جارٍ النشر…",
  "ai.instructions.published": "تم نشر التعليمات (الإصدار {n}).",
  "ai.instructions.publishError": "فشل النشر.",
  "ai.instructions.empty.title": "لا توجد تعليمات منشورة",
  "ai.instructions.empty.desc": "اكتب الإجراءات الداخلية للمختبر أدناه ثم انشرها.",
  "ai.instructions.required": "المحتوى مطلوب.",
  "ai.instructions.unchanged": "لا تغييرات للنشر.",
  "ai.instructions.dirty": "تغييرات غير منشورة.",
  "ai.instructions.reset": "إعادة تعيين",
  "ai.instructions.previewEmpty": "ستظهر المعاينة هنا.",
  "ai.instructions.readonly": "للقراءة فقط — ليس لديك صلاحية النشر.",
};

let registered = false;

/** Register the ai-assistant FR/AR namespaces. Idempotent (safe to call from tests + onRegister). */
export function registerAiAssistantMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
